import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listarAberturas } from '../../api/aberturas.js';
import type { Abertura, FiltroDeAberturas } from '../../api/aberturas.js';
import { intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { filialDoFiltro, rotuloDoLocal } from '../../componentes/deposito.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import {
  CampoBusca,
  CampoFilial,
  ContadorRegistros,
  FiltroSelecao,
} from '../../componentes/Filtros.js';
import { IconeMovimentacoes } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import { AutorDaMovimentacao } from './AutorDaMovimentacao.js';
import { contagemDeMovimentacoes, inicioDoPeriodo, PERIODOS } from './formato.js';
import { escolhido, TODOS, useOpcoesDeOperador } from './opcoesDeFiltro.js';
import { caminhoDaAbertura } from './recorte.js';
import { ResumoNoErp } from './ResumoNoErp.js';
import { Quantidade } from './SaldoDaAbertura.js';
import {
  OPCOES_DE_SITUACAO,
  rotuloDaSituacao,
  SITUACAO_TODAS,
  statusDoFiltro,
  tomDaSituacao,
} from './situacao-da-abertura.js';
import { useValorAdiado } from './useValorAdiado.js';

/**
 * Sem corte de período por padrão, ao contrário da etapa única: uma
 * movimentação iniciada há três semanas e nunca entregue é exatamente a que
 * precisa aparecer, e um recorte de 7 dias a esconderia.
 */
const PERIODO_DAS_ABERTURAS = 'todos';

const COLUNAS: readonly Coluna<Abertura>[] = [
  {
    chave: 'registradoEm',
    rotulo: 'Início',
    largura: '130px',
    celula: (abertura) => (
      <span className="movimentacoes__horario">{formatarDataHora(abertura.registradoEm)}</span>
    ),
  },
  {
    chave: 'produto',
    rotulo: 'Produto',
    largura: '14%',
    celula: (abertura) => (
      <Link className="tabela__abrir" to={caminhoDaAbertura(abertura.id)}>
        {abertura.produto}
      </Link>
    ),
  },
  {
    chave: 'origem',
    rotulo: 'Origem',
    largura: '13%',
    celula: (abertura) => (
      <span className="mono">{rotuloDoLocal(abertura.filialOrigem, abertura.depositoOrigem)}</span>
    ),
  },
  {
    chave: 'iniciada',
    rotulo: 'Iniciada',
    largura: '84px',
    alinhamento: 'direita',
    celula: (abertura) => <Quantidade rotulo="Iniciada" valor={abertura.quantidade} />,
  },
  {
    chave: 'entregue',
    rotulo: 'Entregue',
    largura: '84px',
    alinhamento: 'direita',
    celula: (abertura) => <Quantidade rotulo="Entregue" valor={abertura.entregue} />,
  },
  {
    chave: 'pendente',
    rotulo: 'Pendente',
    largura: '84px',
    alinhamento: 'direita',
    celula: (abertura) => <Quantidade rotulo="Pendente" valor={abertura.pendente} />,
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    largura: '150px',
    celula: (abertura) => (
      <ChipStatus
        tom={tomDaSituacao(abertura.status)}
        rotulo={rotuloDaSituacao(abertura.status)}
        tamanho="tabela"
      />
    ),
  },
  {
    chave: 'erp',
    rotulo: 'Entregas no ERP',
    celula: (abertura) => <ResumoNoErp status={abertura.statusNoErp} />,
  },
  {
    chave: 'autor',
    rotulo: 'Iniciada por',
    largura: '15%',
    celula: (abertura) => <AutorDaMovimentacao autor={abertura} />,
  },
];

/** A divergência espera gente — não pode se parecer com a rotina. */
function classeDaLinha(abertura: Abertura): string | undefined {
  return abertura.status === 'EmDivergencia' ? 'aberturas__linha--divergencia' : undefined;
}

/**
 * Movimentações em duas etapas (r1-mov): iniciadas no coletor e entregues em
 * uma ou mais partes, cada uma com o próprio destino.
 *
 * A tabela responde duas perguntas lado a lado: "quanto falta entregar?"
 * (iniciada, entregue, pendente e a situação) e "o ERP já sabe?" (as entregas
 * por estado no caminho até o Senior). O detalhe abre pelo produto.
 */
export function AbaDuasEtapas() {
  const [situacao, definirSituacao] = useState<string>(SITUACAO_TODAS);
  const [periodo, definirPeriodo] = useState(PERIODO_DAS_ABERTURAS);
  const [produto, definirProduto] = useState('');
  const [deposito, definirDeposito] = useState('');
  const [filial, definirFilial] = useState('');
  const [operadorId, definirOperadorId] = useState(TODOS);
  const [pagina, definirPagina] = useState(1);

  const produtoAdiado = useValorAdiado(produto);
  const depositoAdiado = useValorAdiado(deposito);
  const codFil = filialDoFiltro(filial);

  const filtro = useMemo<FiltroDeAberturas>(
    () => ({
      pagina,
      ...seInformado('status', statusDoFiltro(situacao)),
      ...seInformado('de', inicioDoPeriodo(periodo)),
      ...seInformado('produto', produtoAdiado),
      ...seInformado('deposito', depositoAdiado),
      ...seInformado('codFil', codFil),
      ...seInformado('operadorId', escolhido(operadorId)),
    }),
    [pagina, situacao, periodo, produtoAdiado, depositoAdiado, codFil, operadorId],
  );

  const buscar = useCallback(() => listarAberturas(filtro), [filtro]);
  const consulta = useConsulta(buscar);
  const opcoesDeOperador = useOpcoesDeOperador();

  /** Trocar o recorte volta para a primeira página. */
  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <div className="movimentacoes__aba">
      <FaixaDeFiltros rotulo="Filtros das movimentações em duas etapas">
        <FiltroSelecao
          rotulo="Situação"
          valor={situacao}
          opcoes={OPCOES_DE_SITUACAO}
          aoMudar={(valor) => {
            recortar(() => {
              definirSituacao(valor);
            });
          }}
        />
        <CampoBusca
          rotulo="Buscar por produto"
          valor={produto}
          aoMudar={(valor) => {
            recortar(() => {
              definirProduto(valor);
            });
          }}
          largura={200}
        />
        <CampoFilial
          valor={filial}
          aoMudar={(valor) => {
            recortar(() => {
              definirFilial(valor);
            });
          }}
        />
        <CampoBusca
          rotulo="Buscar por depósito"
          valor={deposito}
          aoMudar={(valor) => {
            recortar(() => {
              definirDeposito(valor);
            });
          }}
          largura={180}
        />
        <FiltroSelecao
          rotulo="Período"
          valor={periodo}
          opcoes={PERIODOS}
          aoMudar={(valor) => {
            recortar(() => {
              definirPeriodo(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Operador"
          valor={operadorId}
          opcoes={opcoesDeOperador}
          aoMudar={(valor) => {
            recortar(() => {
              definirOperadorId(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>{contagemDeMovimentacoes(consulta.dados.total)}</ContadorRegistros>
        )}
      </FaixaDeFiltros>

      <Resultado
        consulta={consulta}
        espera="Carregando movimentações…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeMovimentacoes tamanho={28} />}
            titulo="Nenhuma movimentação em duas etapas neste recorte."
            detalhe="Limpe os filtros para ver mais registros. O depósito acha a movimentação pela origem ou pelo destino de qualquer entrega."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={COLUNAS}
            chaveDe={(abertura) => abertura.id}
            descricao="Movimentações em duas etapas"
            classeDaLinha={classeDaLinha}
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'movimentação', 'movimentações')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </div>
  );
}
